import { BUILDINGS, buildingFootprint, farmYieldMult, isBoxOrder } from '../../shared/constants'
import { Terrain } from '../../shared/terrain'
import type { BuildingType, Resource } from '../../shared/types'
import type { GameState } from '../state'
import { BUILD_ICON, RES_ICON } from '../ui/panels'
import { sitePrepPhase, terrainAt, treeAt } from './terrain'

export interface BuildPreviewCard {
  name: string
  icon: string
  output: { icon: string; name: string; rate: string }[]
  staffing?: string
  soilBonus?: number
  preparation: string
  ready: boolean
}

/** One presentation model for both renderers; production estimates stay unchanged. */
export function placementPreview(st: GameState, type: BuildingType, q: number, r: number, dir?: number): BuildPreviewCard | null {
  if (type === 'road' || isBoxOrder(type)) return null
  const def = BUILDINGS[type]
  const cells = buildingFootprint(type, q, r, dir)
  const mult = type === 'farm' ? farmYieldMult(cells.map(p => terrainAt(st, p.q, p.r))) : 1
  const slots = def.workerSlots ?? 1
  const equippedFood = type === 'farm' || type === 'pier'
  const output = Object.entries(def.produces ?? {}).filter(([, rate]) => !!rate).map(([res, rate]) => {
    const perMin = rate * slots * mult * 60 * (equippedFood ? 2 : 1)
    return { icon: RES_ICON[res as Resource], name: res, rate: perMin >= .95 ? String(Math.round(perMin)) : perMin.toFixed(1) }
  })
  const trees = cells.filter(p => treeAt(st, p.q, p.r)).length
  const level = sitePrepPhase(st, { type, q, r, dir, done: false }) === 'level'
  return {
    name: def.name,
    icon: BUILD_ICON[type],
    output,
    staffing: output.length ? type === 'farm' ? `${slots} level-5 farmers · hoes equipped` : `${slots} worker${slots === 1 ? '' : 's'} · ${equippedFood ? 'rods equipped' : 'full staffing'}` : undefined,
    soilBonus: type === 'farm' ? Math.round((mult / farmYieldMult(cells.map(() => Terrain.Grass)) - 1) * 100) : undefined,
    preparation: trees ? `Clear ${trees} tree${trees === 1 ? '' : 's'}` : level ? 'Level ground' : 'Site ready',
    ready: !trees && !level,
  }
}

/** A readable, pointer-transparent card in CSS pixels, anchored to the build ghost. */
export function drawBuildPreviewCard(ctx: CanvasRenderingContext2D, card: BuildPreviewCard, anchorX: number, anchorY: number, viewportWidth: number, viewportHeight: number): void {
  const width = Math.min(286, viewportWidth - 24)
  const outputHeight = card.output.length ? card.output.length * 37 + 25 : 0
  const height = 40 + outputHeight + 65
  // Keep the card readable and fully on-screen, even in a very small game view.
  if (width < 260 || viewportHeight < height + 24) return
  const x = Math.max(12, Math.min(viewportWidth - width - 12, anchorX - width / 2))
  const topLimit = Math.min(84, Math.max(12, viewportHeight - height - 12))
  const above = anchorY - height - 10 >= topLimit
  const y = Math.max(topLimit, Math.min(viewportHeight - height - 12, above ? anchorY - height - 10 : anchorY + 20))
  const left = x + 14
  const right = x + width - 14
  const gold = '#dec58c', ink = '#f2ede1', muted = '#a5b4a8', green = '#c5e79a'

  ctx.save()
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.shadowColor = 'rgba(0, 0, 0, .38)'
  ctx.shadowBlur = 18
  ctx.shadowOffsetY = 5
  const background = ctx.createLinearGradient(x, y, x, y + height)
  background.addColorStop(0, 'rgba(28, 42, 33, .98)')
  background.addColorStop(1, 'rgba(13, 23, 20, .97)')
  ctx.fillStyle = background
  ctx.beginPath(); ctx.roundRect(x, y, width, height, 12); ctx.fill()
  ctx.shadowBlur = 0; ctx.shadowOffsetY = 0
  ctx.strokeStyle = 'rgba(206, 190, 140, .5)'; ctx.lineWidth = 1
  ctx.stroke()
  // A small pointer keeps the card visually attached to the selected plot.
  const tipX = Math.max(x + 20, Math.min(right - 6, anchorX))
  ctx.fillStyle = above ? '#0d1714' : '#1c2a21'
  ctx.beginPath()
  ctx.moveTo(tipX - 6, above ? y + height - 1 : y + 1)
  ctx.lineTo(tipX, above ? y + height + 6 : y - 6)
  ctx.lineTo(tipX + 6, above ? y + height - 1 : y + 1)
  ctx.fill()

  ctx.font = '16px system-ui, sans-serif'; ctx.fillStyle = ink
  ctx.fillText(card.icon, left, y + 21)
  ctx.font = '600 13px system-ui, sans-serif'
  ctx.fillText(card.name, left + 26, y + 21, width - 135)
  ctx.font = '600 9px system-ui, sans-serif'; ctx.fillStyle = gold; ctx.textAlign = 'right'
  ctx.fillText('BUILD PREVIEW', right, y + 21)
  ctx.textAlign = 'left'

  let outputY = y + 56
  for (const [index, output] of card.output.entries()) {
    ctx.fillStyle = 'rgba(170, 210, 126, .1)'
    ctx.beginPath(); ctx.roundRect(left, outputY - 14, 30, 30, 8); ctx.fill()
    ctx.font = '19px system-ui, sans-serif'; ctx.fillStyle = ink
    ctx.fillText(output.icon, left + 4, outputY + 1)
    ctx.font = '700 26px system-ui, sans-serif'; ctx.fillStyle = green
    const number = `+${output.rate}`
    ctx.fillText(number, left + 41, outputY)
    const numberWidth = ctx.measureText(number).width
    ctx.font = '12px system-ui, sans-serif'; ctx.fillStyle = ink
    ctx.fillText(`${output.name} / min`, left + 46 + numberWidth, outputY + 4)
    if (index === 0 && card.soilBonus !== undefined) {
      const bonus = card.soilBonus > 0
      const badgeX = right - 62
      ctx.fillStyle = bonus ? 'rgba(150, 188, 93, .13)' : 'rgba(255, 255, 255, .05)'
      ctx.beginPath(); ctx.roundRect(badgeX, outputY - 17, 62, 34, 7); ctx.fill()
      ctx.textAlign = 'center'; ctx.fillStyle = bonus ? green : muted
      ctx.font = '700 12px system-ui, sans-serif'
      ctx.fillText(bonus ? `+${card.soilBonus}%` : '100%', badgeX + 31, outputY - 4)
      ctx.font = '9px system-ui, sans-serif'
      ctx.fillText(bonus ? 'fertile soil' : 'soil yield', badgeX + 31, outputY + 9)
      ctx.textAlign = 'left'
    }
    outputY += 37
  }
  if (card.staffing) {
    ctx.font = '11px system-ui, sans-serif'; ctx.fillStyle = muted
    ctx.fillText(card.staffing, left, y + 40 + outputHeight - 12)
  }

  const divider = y + 40 + outputHeight
  ctx.strokeStyle = 'rgba(204, 215, 188, .13)'
  ctx.beginPath(); ctx.moveTo(left, divider); ctx.lineTo(right, divider); ctx.stroke()
  ctx.font = '600 9px system-ui, sans-serif'; ctx.fillStyle = muted
  ctx.fillText('CONSTRUCTION', left, divider + 15)
  const steps = [card.preparation, 'Deliver', 'Build']
  const col = (width - 28) / 3
  for (let i = 0; i < steps.length; i++) {
    const cx = left + col * (i + .5)
    ctx.fillStyle = i === 0 && card.ready ? green : gold
    ctx.textAlign = 'center'; ctx.font = '600 10px system-ui, sans-serif'
    ctx.fillText(i === 0 && card.ready ? '✓' : String(i + 1), cx, divider + 32)
    ctx.font = '10px system-ui, sans-serif'; ctx.fillStyle = i === 0 && card.ready ? green : ink
    ctx.fillText(steps[i], cx, divider + 49, col - 8)
    if (i < 2) {
      ctx.fillStyle = '#6d7e6e'; ctx.fillText('›', left + col * (i + 1), divider + 41)
    }
  }
  ctx.restore()
}
